import { z } from 'zod'
import { asEnumType, asObjectType } from '@gqloom/zod'
import { serviceTemplateTypeEnumSchema } from './service-template-schemas'
import { creditTypeEnumSchema } from './service-credit-template-schemas'
import { numericSchema } from '@hapana/shared/backend/spanner'
import { BookingStatusEnum } from '../bookings/booking-schemas'

/**
 * Service - Instance of purchased service/membership for a client
 */

export const ServiceStatus = {
  Active: 'ACTIVE',
  Suspended: 'SUSPENDED',
  Expired: 'EXPIRED',
  Canceled: 'CANCELED',
  Completed: 'COMPLETED',
  PendingStartDate: 'PENDING_START_DATE',
} as const

export type ServiceStatusValue = (typeof ServiceStatus)[keyof typeof ServiceStatus]

export const serviceStatusEnumSchema = z
  .enum(ServiceStatus)
  .register(asEnumType, { name: 'ServiceStatus' })

/**
 * Service Status Reason - Used for status log audit trail
 */
export const ServiceStatusReason = {
  Purchase: 'PURCHASE',
  ManualSuspension: 'MANUAL_SUSPENSION',
  ScheduledSuspension: 'SCHEDULED_SUSPENSION',
  ManualResume: 'MANUAL_RESUME',
  CreditCompletion: 'CREDIT_COMPLETION',
  Expiration: 'EXPIRATION',
  Cancellation: 'CANCELLATION',
  ScheduledCancellation: 'SCHEDULED_CANCELLATION',
  AutoRenewed: 'AUTO_RENEWED',
  PaymentFailure: 'PAYMENT_FAILURE',
  PaymentProcessing: 'PAYMENT_PROCESSING',
  CreditRestoration: 'CREDIT_RESTORATION',
  SystemExpiration: 'SYSTEM_EXPIRATION',
  PendingStartDate: 'PENDING_START_DATE',
  StartDateArrived: 'START_DATE_ARRIVED',
  InvoiceVoided: 'INVOICE_VOIDED',
  PlanChange: 'PLAN_CHANGE',
} as const

export type ServiceStatusReasonValue =
  (typeof ServiceStatusReason)[keyof typeof ServiceStatusReason]

export const serviceStatusReasonEnumSchema = z
  .enum(ServiceStatusReason)
  .register(asEnumType, { name: 'ServiceStatusReason' })

/**
 * Credit Balance Allocation Snapshot - Individual allocation balance for UI
 */
export const creditBalanceAllocationSnapshotSchema = z
  .object({
    allocationId: z.string().uuid(),
    sessionTypeId: z.string().uuid().nullish(),
    sessionTypeIds: z.array(z.string().uuid()).nullish(),
    sessionTypeName: z.string().optional(),
    creditType: creditTypeEnumSchema,
    balance: z.number().int().nullish(),
    allocated: z.number().int().nullish(),
    consumed: z.number().int(),
    expiresAt: z.string().nullish(),
    activateOnFirstUse: z.boolean().optional(),
    expirationAmount: z.number().int().nullish(),
    expirationUnit: z.string().nullish(),
    validFrom: z.string().nullish(),
    validTo: z.string().nullish(),
  })
  .register(asObjectType, {
    name: 'CreditBalanceAllocationSnapshot',
    description: 'Individual allocation balance snapshot for UI rendering',
  })

export type CreditBalanceAllocationSnapshotSchema = z.infer<
  typeof creditBalanceAllocationSnapshotSchema
>

/**
 * Credit Balance Snapshot - Optimized structure for UI rendering
 * Stored as JSON in Service.creditBalanceSnapshot column
 */
export const creditBalanceSnapshotSchema = z
  .object({
    allocations: z.array(creditBalanceAllocationSnapshotSchema),
    totalBalance: z.number().int().nullish(),
    hasUnlimited: z.boolean(),
    lastUpdated: z.string(),
  })
  .register(asObjectType, {
    name: 'CreditBalanceSnapshot',
    description: 'Optimized credit balance structure for UI rendering',
  })

export type CreditBalanceSnapshotSchema = z.infer<typeof creditBalanceSnapshotSchema>

export const serviceSchema = z
  .object({
    serviceId: z.string().uuid(),
    serviceTemplateId: z.string().uuid(),
    clientId: z.string().uuid(),
    siteId: z.string().uuid(),
    status: serviceStatusEnumSchema,
    purchasedAt: z.date(),
    startsAt: z.string(), // DATE type - ISO date string
    // Display-only. For ON_FIRST_USE packages: null until first credit consumption, then set to the session date.
    // For all other packages: always equals startsAt. Use startsAt for business logic (billing, expiration, sorting).
    activatedStartDate: z.string().nullish(),
    expiresAt: z.string().nullish(), // DATE type
    autoRenew: z.boolean(),
    autoRenewedAt: z.date().nullish(),
    autoRenewedToServiceId: z.string().uuid().nullish(),
    nextBillingDate: z.string().nullish(), // DATE type
    billingCycleAmount: z.number().int().nullish(),
    billingCycleUnit: z.string().nullish(),
    lastAllocationDate: z.date().nullish(),
    createdByInvoiceId: z.string().uuid(),
    canceledAt: z.date().nullish(),
    canceledByStaffUserId: z.string().uuid().nullish(),
    canceledByClientId: z.string().uuid().nullish(),
    cancellationReason: z.string().nullish(),
    // Task 1.1: Service Snapshots
    serviceTemplateSnapshot: z.string().nullish(), // JSON stored as string
    serviceCreditAllocationSnapshot: z.string().nullish(), // JSON stored as string
    // Task 3.4: Pro-rata fields
    creditAllocationStartDate: z.string().nullish(), // DATE type
    serviceFirstBillingDate: z.string().nullish(), // DATE type
    creditValidOverrideEndDate: z.string().nullish(), // DATE type
    // Task 3.2: Credits on Hold
    onHold: z.boolean().default(false),
    // VNG-1365: Agreement tracking
    signedClientAgreementId: z.string().uuid().nullish(),
    contractSignedAt: z.date().nullish(),
    // Credit Balance Snapshot - Real-time credit state for UI performance
    creditBalanceSnapshot: creditBalanceSnapshotSchema.nullish(),
    // Denormalized price fields - populated at service creation time
    originalPrice: numericSchema.nullish(), // ServiceTemplate.price (list price)
    soldPrice: numericSchema.nullish(), // InvoiceItem.amount (actual amount paid)
    soldCurrency: z.string().nullish(), // Invoice.currency (3-char code)
    // Denormalized shared site fields for query performance
    isAllSharedSites: z.boolean().default(false),
    sharedSiteIds: z.array(z.string().uuid()).default([]),
    createdAt: z.date(),
    updatedAt: z.date(),
  })
  .register(asObjectType, {
    name: 'Service',
    description: 'Instance of purchased service/membership for a client',
  })

export type ServiceSchema = z.infer<typeof serviceSchema>

export const serviceCreateSchema = z
  .object({
    serviceId: z.string().uuid(),
    serviceTemplateId: z.string().uuid(),
    clientId: z.string().uuid(),
    siteId: z.string().uuid(),
    status: serviceStatusEnumSchema,
    purchasedAt: z.date(),
    startsAt: z.string(),
    activatedStartDate: z.string().optional(),
    expiresAt: z.string().optional(),
    autoRenew: z.boolean(),
    autoRenewedAt: z.date().nullish(),
    autoRenewedToServiceId: z.string().uuid().nullish(),
    nextBillingDate: z.string().optional(),
    billingCycleAmount: z.number().int().optional(),
    billingCycleUnit: z.string().optional(),
    lastAllocationDate: z.date().optional(),
    createdByInvoiceId: z.string().uuid(),
    canceledAt: z.date().optional(),
    canceledByStaffUserId: z.string().uuid().optional(),
    cancellationReason: z.string().optional(),
    // Task 1.1: Service Snapshots
    serviceTemplateSnapshot: z.string().optional(),
    serviceCreditAllocationSnapshot: z.string().optional(),
    // Task 3.4: Pro-rata fields
    creditAllocationStartDate: z.string().optional(),
    serviceFirstBillingDate: z.string().optional(),
    creditValidOverrideEndDate: z.string().optional(),
    // Task 3.2: Credits on Hold
    onHold: z.boolean().optional(),
    // VNG-1365: Agreement tracking
    signedClientAgreementId: z.string().uuid().optional(),
    contractSignedAt: z.date().optional(),
    // Denormalized price fields
    originalPrice: numericSchema.optional(),
    soldPrice: numericSchema.optional(),
    soldCurrency: z.string().optional(),
    // Denormalized shared site fields for query performance
    isAllSharedSites: z.boolean().default(false),
    sharedSiteIds: z.array(z.string().uuid()).default([]),
    createdAt: z.date(),
    updatedAt: z.date(),
  })
  .register(asObjectType, {
    name: 'ServiceCreateInput',
    description: 'Input for creating a service',
  })

export type ServiceCreateSchema = z.infer<typeof serviceCreateSchema>

export const serviceUpdateSchema = z
  .object({
    status: serviceStatusEnumSchema.optional(),
    startsAt: z.string().optional(),
    expiresAt: z.string().optional(),
    nextBillingDate: z.string().optional(),
    lastAllocationDate: z.date().optional(),
    canceledAt: z.date().optional(),
    canceledByStaffUserId: z.string().uuid().optional(),
    canceledByClientId: z.string().uuid().optional(),
    cancellationReason: z.string().optional(),
    // Task 3.4: Pro-rata fields
    creditAllocationStartDate: z.string().optional(),
    activatedStartDate: z.string().nullish(),
    serviceFirstBillingDate: z.string().optional(),
    creditValidOverrideEndDate: z.string().optional(),
    // Task 3.2: Credits on Hold
    onHold: z.boolean().optional(),
    autoRenew: z.boolean().optional(),
    autoRenewedAt: z.date().nullish(),
    autoRenewedToServiceId: z.string().uuid().nullish(),
    // Credit Balance Snapshot
    creditBalanceSnapshot: creditBalanceSnapshotSchema.optional(),
    updatedAt: z.date(),
  })
  .register(asObjectType, {
    name: 'ServiceUpdateInput',
    description: 'Input for updating a service',
  })

export type ServiceUpdateSchema = z.infer<typeof serviceUpdateSchema>

/**
 * ServiceCreditAllocation - Credit allocation event for a service
 */

export const ServiceCreditAllocationReason = {
  Purchase: 'PURCHASE',
  Recurring: 'RECURRING',
  Manual: 'MANUAL',
  Converted: 'CONVERTED',
  Provisional: 'PROVISIONAL',
  PlanChange: 'PLAN_CHANGE',
} as const

export type ServiceCreditAllocationReasonValue =
  (typeof ServiceCreditAllocationReason)[keyof typeof ServiceCreditAllocationReason]

export const serviceCreditAllocationReasonEnumSchema = z
  .enum(ServiceCreditAllocationReason)
  .register(asEnumType, { name: 'ServiceCreditAllocationReason' })

/**
 * When both `expiresAtDate` and `validTo` are set, `expiresAtDate` (hard
 * physical expiry) must be on or after `validTo` (billing-cycle boundary).
 * Applied only to the import-path schema
 * (`serviceCreditAllocationBulkCreateForImportSchema`) — legacy production
 * rows aren't guaranteed to satisfy this invariant, so it is deliberately
 * scoped to the new bulk-import write path.
 */
function allocationExpiryAfterValidToCheck(
  ctx: z.core.ParsePayload<{ expiresAtDate?: unknown; validTo?: unknown }>
): void {
  const exp = ctx.value.expiresAtDate
  const validTo = ctx.value.validTo
  if (typeof exp === 'string' && typeof validTo === 'string' && exp < validTo) {
    ctx.issues.push({
      code: 'custom',
      message: `expiresAtDate (${exp}) must be on or after validTo (${validTo})`,
      path: ['expiresAtDate'],
      input: ctx.value,
    })
  }
}

/**
 * `activateOnFirstUse = true` is PACKAGE-only. Cannot be enforced at the
 * schema layer because the allocation row does not carry a `serviceType`
 * denormalization — at parse time only `serviceId` is known. Enforced via a
 * wrapper assert in `ServicesService.bulkCreateForImport`
 * (`assertActivateOnFirstUseIsPackageOnly`).
 */
export const serviceCreditAllocationSchema = z
  .object({
    serviceCreditAllocationId: z.string().uuid(),
    serviceId: z.string().uuid(),
    serviceCreditTemplateId: z.string().uuid(),
    siteSessionTypeId: z.string().uuid().nullish(), // Legacy - use siteSessionTypeIds
    // Task 1.5: Session Type Array
    siteSessionTypeIds: z.array(z.string().uuid()).nullish(),
    allocatedAt: z.date(),
    expiresAtDate: z.string().nullish(), // DATE type — last usable day (inclusive)
    creditType: z.string(),
    creditAmount: z.number().int().nullish(),
    allocationReason: serviceCreditAllocationReasonEnumSchema,
    // Task 3.4: Pro-rata validity period
    validFrom: z.string().nullish(), // DATE type
    validTo: z.string().nullish(), // DATE type
    // BH-1268: Credit activation on first use
    activateOnFirstUse: z.boolean().nullish(),
    activatedAt: z.date().nullish(),
    // CORE-1320: Billing anchor fields for cascade sync
    recurringInvoiceId: z.uuid().nullish(),
    billingSequenceNumber: z.number().int().nullish(),
    createdAt: z.date(),
  })
  .register(asObjectType, {
    name: 'ServiceCreditAllocation',
    description: 'Credit allocation event for a service',
  })

export type ServiceCreditAllocationSchema = z.infer<typeof serviceCreditAllocationSchema>

export const serviceCreditAllocationCreateSchema = z
  .object({
    serviceCreditAllocationId: z.string().uuid(),
    serviceId: z.string().uuid(),
    serviceCreditTemplateId: z.string().uuid(),
    siteSessionTypeId: z.string().uuid().optional(), // Legacy - use siteSessionTypeIds
    // Task 1.5: Session Type Array
    siteSessionTypeIds: z.array(z.string().uuid()).optional(),
    allocatedAt: z.date(),
    expiresAtDate: z.string().optional(), // DATE type — last usable day (inclusive)
    creditType: z.string(),
    creditAmount: z.number().int().optional(),
    allocationReason: serviceCreditAllocationReasonEnumSchema,
    // Task 3.4: Pro-rata validity period
    validFrom: z.string().optional(),
    validTo: z.string().optional(),
    // BH-1268: Credit activation on first use
    activateOnFirstUse: z.boolean().optional(),
    activatedAt: z.date().optional(),
    // CORE-1320: Billing anchor fields for cascade sync
    recurringInvoiceId: z.uuid().optional(),
    billingSequenceNumber: z.number().int().optional(),
    createdAt: z.date(),
  })
  .register(asObjectType, {
    name: 'ServiceCreditAllocationCreateInput',
    description: 'Input for creating a service credit allocation',
  })

export type ServiceCreditAllocationCreateSchema = z.infer<
  typeof serviceCreditAllocationCreateSchema
>

/**
 * ServiceCreditAllocationAdjustmentLog - Append-only audit trail for cascade date changes
 * Not registered with GQL — internal audit table only.
 */
export const serviceCreditAllocationAdjustmentLogCreateSchema = z.object({
  serviceCreditAllocationAdjustmentLogId: z.uuid(),
  serviceCreditAllocationId: z.uuid(),
  serviceId: z.uuid(),
  triggeredByInvoiceId: z.uuid().nullable(),
  staffUserId: z.uuid().nullable(),
  fieldName: z.enum(['validFrom', 'validTo', 'expiresAtDate']),
  oldValue: z.string().max(10).nullable(), // ISO date string
  newValue: z.string().max(10).nullable(), // ISO date string
  createdAt: z.date(),
})

export type ServiceCreditAllocationAdjustmentLogCreateSchema = z.infer<
  typeof serviceCreditAllocationAdjustmentLogCreateSchema
>

/**
 * ServiceCreditLedger - Immutable transaction ledger for credit usage
 */

export const ServiceCreditLedgerDirection = {
  In: 'IN',
  Out: 'OUT',
} as const

export type ServiceCreditLedgerDirectionValue =
  (typeof ServiceCreditLedgerDirection)[keyof typeof ServiceCreditLedgerDirection]

export const serviceCreditLedgerDirectionEnumSchema = z
  .enum(['IN', 'OUT'])
  .register(asEnumType, { name: 'ServiceCreditLedgerDirection' })

export const ServiceCreditLedgerTransactionType = {
  Allocated: 'ALLOCATED',
  Booking: 'BOOKING',
  Refund: 'REFUND',
  Expired: 'EXPIRED',
  Manual: 'MANUAL',
} as const

export type ServiceCreditLedgerTransactionTypeValue =
  (typeof ServiceCreditLedgerTransactionType)[keyof typeof ServiceCreditLedgerTransactionType]

export const serviceCreditLedgerTransactionTypeEnumSchema = z
  .enum(['ALLOCATED', 'BOOKING', 'REFUND', 'EXPIRED', 'MANUAL'])
  .register(asEnumType, { name: 'ServiceCreditLedgerTransactionType' })

/**
 * Invariants on ledger rows.
 *
 * `transactionType` and `direction` must agree:
 *   - ALLOCATED ⟹ IN  (credits coming in)
 *   - BOOKING   ⟹ OUT (credits consumed by a booking)
 *   - REFUND    ⟹ IN  (refund returns credits)
 *   - EXPIRED   ⟹ OUT (credits being closed out)
 *   - MANUAL    ⟹ either (caller-controlled — excluded from strict check)
 *
 * `amount = NULL ⟺ creditType = 'UNLIMITED'`. UNLIMITED allocations track
 * count-less (no balance); any non-NULL amount is a write-side bug. LIMITED
 * ledger rows must have a numeric amount.
 *
 * Applied only to the import-path schema
 * (`serviceCreditLedgerBulkCreateForImportSchema`) — legacy production rows
 * aren't guaranteed to satisfy these invariants, so it is deliberately scoped
 * to the new bulk-import write path.
 */
function ledgerInvariantsCheck(
  ctx: z.core.ParsePayload<{
    transactionType?: unknown
    direction?: unknown
    amount?: unknown
    creditType?: unknown
  }>
): void {
  const tt = ctx.value.transactionType
  const dir = ctx.value.direction
  const expectedDir =
    tt === 'ALLOCATED' || tt === 'REFUND'
      ? 'IN'
      : tt === 'BOOKING' || tt === 'EXPIRED'
        ? 'OUT'
        : null
  if (expectedDir !== null && dir !== expectedDir) {
    ctx.issues.push({
      code: 'custom',
      message: `direction must be ${expectedDir} for transactionType=${tt} (got ${dir})`,
      path: ['direction'],
      input: ctx.value,
    })
  }

  const ct = ctx.value.creditType
  const amt = ctx.value.amount
  if (ct === 'UNLIMITED' && amt != null) {
    ctx.issues.push({
      code: 'custom',
      message: `amount must be NULL for UNLIMITED ledger rows (got ${amt})`,
      path: ['amount'],
      input: ctx.value,
    })
  }
  if (ct === 'LIMITED' && (amt == null || typeof amt !== 'number')) {
    ctx.issues.push({
      code: 'custom',
      message: `amount is required for LIMITED ledger rows (got ${amt})`,
      path: ['amount'],
      input: ctx.value,
    })
  }
}

export const serviceCreditLedgerSchema = z
  .object({
    serviceCreditLedgerId: z.string().uuid(),
    clientId: z.string().uuid(),
    siteId: z.string().uuid(),
    serviceId: z.string().uuid(),
    serviceCreditAllocationId: z.string().uuid(),
    siteSessionTypeId: z.string().uuid().nullish(),
    direction: serviceCreditLedgerDirectionEnumSchema,
    amount: z.number().int().nullish(),
    balance: z.number().int().nullish(),
    creditType: z.string(),
    transactionType: serviceCreditLedgerTransactionTypeEnumSchema,
    bookingId: z.string().uuid().nullish(),
    waitlistEntryId: z.uuid().nullish(),
    checkInId: z.uuid().nullish(),
    refundId: z.string().uuid().nullish(),
    notes: z.string().nullish(),
    createdAt: z.date(),
    createdByStaffUserId: z.string().uuid().nullish(),
    priority: z.number().int().positive().nullish(),
    // Task 1.4: Ledger Reference Tracking
    allocationReferenceId: z.string().uuid().nullish(),
    relatedLedgerEntryId: z.string().uuid().nullish(),
    // Booking status (from LEFT JOIN, only populated when using findByServiceWithBookingStatus)
    bookingStatus: BookingStatusEnum.nullish(),
    // Session name (from LEFT JOIN, only populated when using findByServiceWithBookingStatus)
    sessionName: z.string().nullish(),
    sessionDate: z.string().nullish(),
    sessionStartTime: z.coerce.date().nullish(),
  })
  .register(asObjectType, {
    name: 'ServiceCreditLedger',
    description: 'Immutable transaction ledger for credit usage',
  })

export type ServiceCreditLedgerSchema = z.infer<typeof serviceCreditLedgerSchema>

export const serviceCreditLedgerCreateSchema = z
  .object({
    serviceCreditLedgerId: z.string().uuid(),
    clientId: z.string().uuid(),
    siteId: z.string().uuid(),
    serviceId: z.string().uuid(),
    serviceCreditAllocationId: z.string().uuid(),
    siteSessionTypeId: z.string().uuid().optional(),
    direction: serviceCreditLedgerDirectionEnumSchema,
    amount: z.number().int().optional(),
    balance: z.number().int().optional(),
    creditType: z.string(),
    transactionType: serviceCreditLedgerTransactionTypeEnumSchema,
    bookingId: z.string().uuid().optional(),
    waitlistEntryId: z.uuid().optional(),
    checkInId: z.uuid().optional(),
    refundId: z.string().uuid().optional(),
    notes: z.string().optional(),
    createdAt: z.date(),
    createdByStaffUserId: z.string().uuid().optional(),
    priority: z.number().int().positive().optional(),
    // Task 1.4: Ledger Reference Tracking
    allocationReferenceId: z.string().uuid().optional(),
    relatedLedgerEntryId: z.string().uuid().optional(),
  })
  .register(asObjectType, {
    name: 'ServiceCreditLedgerCreateInput',
    description: 'Input for creating a service credit ledger entry',
  })

export type ServiceCreditLedgerCreateSchema = z.infer<typeof serviceCreditLedgerCreateSchema>

/**
 * ServiceStatusLog - Audit trail for service status changes (Task 1.8)
 */

export const ServiceStatusLogRelatedEntityType = {
  Booking: 'BOOKING',
  Client: 'CLIENT',
  Invoice: 'INVOICE',
  Payment: 'PAYMENT',
  Staff: 'STAFF',
  System: 'SYSTEM',
} as const

export type ServiceStatusLogRelatedEntityTypeValue =
  (typeof ServiceStatusLogRelatedEntityType)[keyof typeof ServiceStatusLogRelatedEntityType]

export const serviceStatusLogRelatedEntityTypeEnumSchema = z
  .enum(['BOOKING', 'CLIENT', 'INVOICE', 'PAYMENT', 'STAFF', 'SYSTEM'])
  .register(asEnumType, { name: 'ServiceStatusLogRelatedEntityType' })

export const serviceStatusLogSchema = z
  .object({
    serviceStatusLogId: z.string().uuid(),
    serviceId: z.string().uuid(),
    clientId: z.string().uuid(),
    siteId: z.string().uuid(),
    previousStatus: serviceStatusEnumSchema.nullish(),
    newStatus: serviceStatusEnumSchema,
    reason: serviceStatusReasonEnumSchema,
    notes: z.string().nullish(),
    relatedEntityId: z.string().uuid().nullish(),
    relatedEntityType: serviceStatusLogRelatedEntityTypeEnumSchema.nullish(),
    createdAt: z.date(),
    createdByStaffUserId: z.string().uuid().nullish(),
    createdByClientId: z.string().uuid().nullish(),
  })
  .register(asObjectType, {
    name: 'ServiceStatusLog',
    description: 'Audit trail for service status changes',
  })

export type ServiceStatusLogSchema = z.infer<typeof serviceStatusLogSchema>

export const serviceStatusLogCreateSchema = z
  .object({
    serviceStatusLogId: z.string().uuid(),
    serviceId: z.string().uuid(),
    clientId: z.string().uuid(),
    siteId: z.string().uuid(),
    previousStatus: serviceStatusEnumSchema.optional(),
    newStatus: serviceStatusEnumSchema,
    reason: serviceStatusReasonEnumSchema,
    notes: z.string().optional(),
    relatedEntityId: z.string().uuid().optional(),
    relatedEntityType: serviceStatusLogRelatedEntityTypeEnumSchema.optional(),
    createdAt: z.date(),
    createdByStaffUserId: z.string().uuid().optional(),
    createdByClientId: z.string().uuid().optional(),
  })
  .register(asObjectType, {
    name: 'ServiceStatusLogCreateInput',
    description: 'Input for creating a service status log entry',
  })

export type ServiceStatusLogCreateSchema = z.infer<typeof serviceStatusLogCreateSchema>

/**
 * Input shape for `ServiceCreditAllocationsRepository.bulkUpsertForImport`,
 * the import path's child-table write for service credit allocations.
 *
 * Mirrors the repo bulk method's input
 * (`Omit<ServiceCreditAllocationCreateSchema, 'createdAt'>`): the repo bulk
 * method assigns `createdAt = new Date()` to match the per-row `createMany()`
 * contract; the caller owns `serviceCreditAllocationId` because the
 * ledger-entry FK references it (transformer needs a stable id to link both
 * writes).
 */
export const serviceCreditAllocationBulkCreateForImportSchema = serviceCreditAllocationCreateSchema
  .omit({ createdAt: true })
  .check(allocationExpiryAfterValidToCheck)

export type ServiceCreditAllocationBulkCreateForImportSchema = z.infer<
  typeof serviceCreditAllocationBulkCreateForImportSchema
>

/**
 * Input shape for `ServiceCreditLedgerRepository.bulkUpsertForImport`, the
 * import path's child-table write for credit ledger entries.
 *
 * Mirrors the repo bulk method's input
 * (`Omit<ServiceCreditLedgerCreateSchema, 'createdAt'>`): the repo bulk
 * method assigns `createdAt = new Date()` to match the per-row
 * `recordTransaction()` contract; the caller owns `serviceCreditLedgerId` for
 * retry idempotency. Unlike `recordTransaction()` the bulk path does NOT
 * lookup the latest balance — transformers pre-compute `balance` per row when
 * constructing the input.
 */
export const serviceCreditLedgerBulkCreateForImportSchema = serviceCreditLedgerCreateSchema
  .omit({
    createdAt: true,
  })
  .check(ledgerInvariantsCheck)

export type ServiceCreditLedgerBulkCreateForImportSchema = z.infer<
  typeof serviceCreditLedgerBulkCreateForImportSchema
>

/**
 * Input shape for `ServiceStatusLogRepository.bulkUpsertForImport`, the
 * import path's child-table write for service status log entries.
 *
 * Mirrors the repo bulk method's input
 * (`Omit<ServiceStatusLogCreateSchema, 'createdAt'>`): the repo bulk method
 * assigns `createdAt = new Date()` to match the per-row `create()` contract;
 * the caller owns `serviceStatusLogId` for retry idempotency.
 */
export const serviceStatusLogBulkCreateForImportSchema = serviceStatusLogCreateSchema.omit({
  createdAt: true,
})

export type ServiceStatusLogBulkCreateForImportSchema = z.infer<
  typeof serviceStatusLogBulkCreateForImportSchema
>

/**
 * Input shape for the per-row Service write inside
 * `ServicesService.bulkCreateForImport`.
 *
 * Mirrors `ServicesRepository.bulkUpsertForImport`'s row contract: the repo
 * bulk method assigns `createdAt = updatedAt = new Date()` (matching the
 * per-row `ServicesRepository.create`) and the caller owns `serviceId`
 * (deterministic from the transformer for retry idempotency, and required so
 * sibling-table rows — status log / credit allocation / ledger entries —
 * can reference it before the parent write completes).
 */
export const serviceBulkCreateForImportSchema = serviceCreateSchema.omit({
  createdAt: true,
  updatedAt: true,
})

export type ServiceBulkCreateForImportSchema = z.infer<typeof serviceBulkCreateForImportSchema>

/**
 * Credit balance snapshot for GraphQL output
 */
export const creditBalanceSnapshotOutputSchema = z
  .object({
    totalBalance: z.number().int().nullish(),
    hasUnlimited: z.boolean(),
    lastUpdated: z.string().nullish(),
    allocations: z
      .array(
        z.object({
          allocationId: z.string().uuid(),
          sessionTypeId: z.string().uuid().nullish(),
          sessionTypeIds: z.array(z.string().uuid()).nullish(),
          sessionTypeName: z.string().nullish(),
          creditType: z.string(),
          balance: z.number().int().nullish(),
          allocated: z.number().int().nullish(),
          consumed: z.number().int(),
          expiresAt: z.string().nullish(),
          activateOnFirstUse: z.boolean().optional(),
          expirationAmount: z.number().int().nullish(),
          expirationUnit: z.string().nullish(),
          validFrom: z.string().nullish(),
          validTo: z.string().nullish(),
        })
      )
      .optional(),
  })
  .register(asObjectType, {
    name: 'CreditBalanceSnapshotOutput',
    description: 'Credit balance snapshot for a service',
  })

export type CreditBalanceSnapshotOutputSchema = z.infer<typeof creditBalanceSnapshotOutputSchema>

/**
 * Service with template and client information for list queries
 * This is a separate schema for GraphQL output to avoid z.any() types
 */
export const serviceWithTemplateAndClientSchema = z
  .object({
    serviceId: z.string().uuid(),
    serviceTemplateId: z.string().uuid(),
    clientId: z.string().uuid(),
    siteId: z.string().uuid(),
    status: serviceStatusEnumSchema,
    purchasedAt: z.date(),
    startsAt: z.string(),
    activatedStartDate: z.string().nullish(),
    expiresAt: z.string().nullish(),
    autoRenew: z.boolean(),
    nextBillingDate: z.string().nullish(),
    billingCycleAmount: z.number().int().nullish(),
    billingCycleUnit: z.string().nullish(),
    lastAllocationDate: z.date().nullish(),
    createdByInvoiceId: z.string().uuid(),
    canceledAt: z.date().nullish(),
    canceledByStaffUserId: z.string().uuid().nullish(),
    cancellationReason: z.string().nullish(),
    onHold: z.boolean(),
    creditBalanceSnapshot: creditBalanceSnapshotOutputSchema.nullish(),
    createdAt: z.date(),
    updatedAt: z.date(),
    serviceTemplate: z
      .object({
        name: z.string(),
        type: serviceTemplateTypeEnumSchema,
      })
      .nullish(),
    client: z
      .object({
        clientId: z.string().uuid(),
        firstName: z.string().nullish(),
        lastName: z.string().nullish(),
        email: z.string().nullish(),
        alerts: z
          .array(
            z.object({
              clientId: z.string(),
              type: z.string(),
              data: z.record(z.string(), z.unknown()).optional(),
              requiresFetch: z.boolean().default(false),
            })
          )
          .optional(),
      })
      .nullish(),
  })
  .register(asObjectType, {
    name: 'ServiceWithTemplateAndClient',
    description: 'Service with associated template and client information',
  })

export type ServiceWithTemplateAndClientSchema = z.infer<typeof serviceWithTemplateAndClientSchema>

/**
 * Input schema for listing services by template type
 */
export const listServicesByTemplateTypeInputSchema = z
  .object({
    cursor: z.string().optional(),
    pageSize: z.number().int().positive().max(100).default(20).optional(),
    search: z.string().optional(),
    templateType: serviceTemplateTypeEnumSchema,
    filter: z
      .object({
        status: serviceStatusEnumSchema.optional(),
        statuses: z.array(serviceStatusEnumSchema).optional(),
      })
      .optional(),
  })
  .register(asObjectType, {
    name: 'ListServicesByTemplateTypeInput',
    description: 'Input for listing services by template type with pagination and filters',
  })

export type ListServicesByTemplateTypeInputSchema = z.infer<
  typeof listServicesByTemplateTypeInputSchema
>

/**
 * Result row for services pagination
 */
export const serviceResultRowSchema = z
  .object({
    row: serviceWithTemplateAndClientSchema,
    cursor: z.string(),
  })
  .register(asObjectType, {
    name: 'ServiceResultRow',
    description: 'Single result row with service data and cursor',
  })

export type ServiceResultRowSchema = z.infer<typeof serviceResultRowSchema>

/**
 * Pagination result for listing services
 */
export const servicesPaginationResultSchema = z
  .object({
    pageInfo: z.object({
      startCursor: z.string().nullish(),
      endCursor: z.string().nullish(),
      totalRecords: z.number().int(),
      hasNextPage: z.boolean(),
    }),
    results: z.array(serviceResultRowSchema),
  })
  .register(asObjectType, {
    name: 'ServicesPaginationResult',
    description: 'Paginated list of services',
  })

export type ServicesPaginationResultSchema = z.infer<typeof servicesPaginationResultSchema>

/**
 * ServiceSuspension - Tracks scheduled and active suspensions for services
 */

export const ServiceSuspensionStatus = {
  Scheduled: 'SCHEDULED',
  Active: 'ACTIVE',
  Completed: 'COMPLETED',
  Canceled: 'CANCELED',
} as const

export type ServiceSuspensionStatusValue =
  (typeof ServiceSuspensionStatus)[keyof typeof ServiceSuspensionStatus]

export const serviceSuspensionStatusEnumSchema = z
  .enum(['SCHEDULED', 'ACTIVE', 'COMPLETED', 'CANCELED'])
  .register(asEnumType, { name: 'ServiceSuspensionStatus' })

export const ServiceSuspensionChargeMode = {
  Full: 'FULL',
  Partial: 'PARTIAL',
  None: 'NONE',
} as const

export type ServiceSuspensionChargeModeValue =
  (typeof ServiceSuspensionChargeMode)[keyof typeof ServiceSuspensionChargeMode]

export const serviceSuspensionChargeModeEnumSchema = z
  .enum(['FULL', 'PARTIAL', 'NONE'])
  .register(asEnumType, { name: 'ServiceSuspensionChargeMode' })

export const serviceSuspensionSchema = z
  .object({
    serviceSuspensionId: z.string().uuid(),
    serviceId: z.string().uuid(),
    status: serviceSuspensionStatusEnumSchema,
    startDate: z.string(), // DATE type - ISO date string
    endDate: z.string().nullish(), // DATE type - NULL = indefinite
    suspensionReason: z.string().nullish(),
    chargeMode: serviceSuspensionChargeModeEnumSchema.nullish(),
    chargeAmount: numericSchema.nullish(), // NUMERIC type
    createdAt: z.date(),
    createdByStaffUserId: z.string().uuid().nullish(),
    activatedAt: z.date().nullish(),
    completedAt: z.date().nullish(),
  })
  .register(asObjectType, {
    name: 'ServiceSuspension',
    description: 'Tracks scheduled and active suspensions for services',
  })

export type ServiceSuspensionSchema = z.infer<typeof serviceSuspensionSchema>

export const serviceSuspensionCreateSchema = serviceSuspensionSchema.omit({
  serviceSuspensionId: true,
  createdAt: true,
})

export type ServiceSuspensionCreateSchema = z.infer<typeof serviceSuspensionCreateSchema>

export const serviceSuspensionUpdateSchema = z
  .object({
    status: serviceSuspensionStatusEnumSchema.optional(),
    endDate: z.string().optional(),
    suspensionReason: z.string().optional(),
    chargeMode: serviceSuspensionChargeModeEnumSchema.optional(),
    chargeAmount: numericSchema.optional(),
    activatedAt: z.date().optional(),
    completedAt: z.date().optional(),
  })
  .register(asObjectType, {
    name: 'ServiceSuspensionUpdateInput',
    description: 'Input for updating a service suspension',
  })

export type ServiceSuspensionUpdateSchema = z.infer<typeof serviceSuspensionUpdateSchema>

export const suspendServiceInputSchema = serviceSuspensionSchema
  .pick({
    serviceId: true,
    startDate: true,
    endDate: true,
    chargeMode: true,
    chargeAmount: true,
  })
  .extend({
    siteId: z.string().uuid(),
    reason: z.string().optional(), // Maps to suspensionReason
    bookingIds: z.array(z.string().uuid()).optional(),
    failedInvoiceIds: z.array(z.string().uuid()).optional(),
  })
  .partial({
    startDate: true,
    endDate: true,
    chargeMode: true,
    chargeAmount: true,
  })
  .register(asObjectType, {
    name: 'SuspendServiceInput',
    description: 'Input for suspending a service',
  })

export type SuspendServiceInputSchema = z.infer<typeof suspendServiceInputSchema>

/**
 * ServiceCancellation - Tracks scheduled cancellations for services
 */

export const ServiceCancellationStatus = {
  Scheduled: 'SCHEDULED',
  Executed: 'EXECUTED',
  Canceled: 'CANCELED',
} as const

export type ServiceCancellationStatusValue =
  (typeof ServiceCancellationStatus)[keyof typeof ServiceCancellationStatus]

export const serviceCancellationStatusEnumSchema = z
  .enum(['SCHEDULED', 'EXECUTED', 'CANCELED'])
  .register(asEnumType, { name: 'ServiceCancellationStatus' })

export const serviceCancellationSchema = z
  .object({
    serviceCancellationId: z.string().uuid(),
    serviceId: z.string().uuid(),
    status: serviceCancellationStatusEnumSchema,
    cancellationDate: z.string(), // DATE type - ISO date string
    cancellationReason: z.string().nullish(),
    createdAt: z.date(),
    createdByStaffUserId: z.string().uuid().nullish(),
    createdByClientId: z.string().uuid().nullish(),
    executedAt: z.date().nullish(),
    canceledAt: z.date().nullish(),
    canceledByStaffUserId: z.string().uuid().nullish(),
    canceledByClientId: z.string().uuid().nullish(),
  })
  .register(asObjectType, {
    name: 'ServiceCancellation',
    description: 'Tracks scheduled cancellations for services',
  })

export type ServiceCancellationSchema = z.infer<typeof serviceCancellationSchema>

export const serviceCancellationCreateSchema = serviceCancellationSchema.omit({
  serviceCancellationId: true,
  createdAt: true,
})

export type ServiceCancellationCreateSchema = z.infer<typeof serviceCancellationCreateSchema>

export const serviceCancellationUpdateSchema = z
  .object({
    status: serviceCancellationStatusEnumSchema.optional(),
    cancellationReason: z.string().optional(),
    executedAt: z.date().optional(),
    canceledAt: z.date().optional(),
    canceledByStaffUserId: z.string().uuid().optional(),
    canceledByClientId: z.string().uuid().optional(),
  })
  .register(asObjectType, {
    name: 'ServiceCancellationUpdateInput',
    description: 'Input for updating a service cancellation',
  })

export type ServiceCancellationUpdateSchema = z.infer<typeof serviceCancellationUpdateSchema>

/**
 * ServicePlanChange - Tracks plan changes (upgrades/downgrades) for services
 */

export const ServicePlanChangeStatus = {
  Scheduled: 'SCHEDULED',
  Executed: 'EXECUTED',
  Canceled: 'CANCELED',
} as const

export type ServicePlanChangeStatusValue =
  (typeof ServicePlanChangeStatus)[keyof typeof ServicePlanChangeStatus]

export const servicePlanChangeStatusEnumSchema = z
  .enum(ServicePlanChangeStatus)
  .register(asEnumType, { name: 'ServicePlanChangeStatus' })

export const ServicePlanChangeEffectiveType = {
  Immediate: 'IMMEDIATE',
  EndOfCycle: 'END_OF_CYCLE',
} as const

export type ServicePlanChangeEffectiveTypeValue =
  (typeof ServicePlanChangeEffectiveType)[keyof typeof ServicePlanChangeEffectiveType]

export const servicePlanChangeEffectiveTypeEnumSchema = z
  .enum(ServicePlanChangeEffectiveType)
  .register(asEnumType, { name: 'ServicePlanChangeEffectiveType' })

export const ServicePlanChangeDirection = {
  Upgrade: 'UPGRADE',
  Downgrade: 'DOWNGRADE',
} as const

export type ServicePlanChangeDirectionValue =
  (typeof ServicePlanChangeDirection)[keyof typeof ServicePlanChangeDirection]

export const servicePlanChangeDirectionEnumSchema = z
  .enum(ServicePlanChangeDirection)
  .register(asEnumType, { name: 'ServicePlanChangeDirection' })

export const servicePlanChangeSchema = z
  .object({
    servicePlanChangeId: z.uuid(),
    serviceId: z.uuid(),
    fromServiceTemplateId: z.uuid(),
    toServiceTemplateId: z.uuid(),
    fromServiceTemplateSnapshot: z.string().nullish(), // JSON stored as string
    toServiceTemplateSnapshot: z.string().nullish(), // JSON stored as string
    status: servicePlanChangeStatusEnumSchema,
    effectiveType: servicePlanChangeEffectiveTypeEnumSchema,
    effectiveDate: z.string(), // DATE type - ISO date string
    reason: z.string().nullish(),
    customPrice: numericSchema.nullish(), // NUMERIC type
    initiatedByStaffUserId: z.uuid(),
    executedAt: z.date().nullish(),
    canceledAt: z.date().nullish(),
    proratedInvoiceId: z.uuid().nullish(),
    canceledByStaffUserId: z.uuid().nullish(),
    direction: servicePlanChangeDirectionEnumSchema.nullish(),
    fromExpiresAt: z.string().nullish(), // DATE — service.expiresAt before mutation
    toExpiresAt: z.string().nullish(), // DATE — service.expiresAt after mutation
    signedClientAgreementId: z.uuid().nullish(),
    createdAt: z.date(),
  })
  .register(asObjectType, {
    name: 'ServicePlanChange',
    description: 'Tracks plan changes for services',
  })

export type ServicePlanChangeSchema = z.infer<typeof servicePlanChangeSchema>

export const servicePlanChangeCreateSchema = servicePlanChangeSchema.omit({
  servicePlanChangeId: true,
  createdAt: true,
})

export type ServicePlanChangeCreateSchema = z.infer<typeof servicePlanChangeCreateSchema>

export const servicePlanChangeUpdateSchema = z
  .object({
    status: servicePlanChangeStatusEnumSchema.optional(),
    executedAt: z.date().optional(),
    canceledAt: z.date().optional(),
    proratedInvoiceId: z.uuid().optional(),
    canceledByStaffUserId: z.uuid().optional(),
    direction: servicePlanChangeDirectionEnumSchema.optional(),
    fromExpiresAt: z.string().nullable().optional(),
    toExpiresAt: z.string().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'ServicePlanChangeUpdateInput',
    description: 'Input for updating a service plan change',
  })

export type ServicePlanChangeUpdateSchema = z.infer<typeof servicePlanChangeUpdateSchema>

export const cancelServiceInputSchema = z
  .object({
    serviceId: z.string().uuid(),
    siteId: z.string().uuid(),
    reason: z.string().optional(),
    cancelAt: z.string().optional(), // DATE type - if future date = schedule, if null/today = immediate
    bookingIds: z.array(z.string().uuid()).optional(),
    failedInvoiceIds: z.array(z.string().uuid()).optional(),
    chargeMode: serviceSuspensionChargeModeEnumSchema.optional(),
    chargeAmount: numericSchema.optional(),
  })
  .register(asObjectType, {
    name: 'CancelServiceInput',
    description: 'Input for cancelling a service with optional scheduling and booking cancellation',
  })

export type CancelServiceInputSchema = z.infer<typeof cancelServiceInputSchema>

/**
 * Client Services Pagination - For Client Profile UI
 */
import {
  createPaginationWithCursorArgsSchema,
  createPaginationWithCursorResultSchema,
} from '../database'

export const templateCreditSessionTypeSchema = z
  .object({
    siteSessionTypeId: z.uuid(),
    siteSessionTypeIds: z.array(z.uuid()),
    name: z.string(),
  })
  .register(asObjectType, { name: 'TemplateCreditSessionType' })

export type TemplateCreditSessionTypeSchema = z.infer<typeof templateCreditSessionTypeSchema>

export const clientServicePublicSchema = z
  .object({
    serviceId: z.string().uuid(),
    serviceTemplateId: z.string().uuid(),
    clientId: z.string().uuid(),
    siteId: z.string().uuid(),
    status: serviceStatusEnumSchema,
    purchasedAt: z.date(),
    startsAt: z.string(),
    activatedStartDate: z.string().nullish(),
    expiresAt: z.string().nullish(),
    autoRenew: z.boolean(),
    nextBillingDate: z.string().nullish(),
    billingCycleAmount: z.number().int().nullish(),
    billingCycleUnit: z.string().nullish(),
    canceledAt: z.date().nullish(),
    cancellationReason: z.string().nullish(),
    onHold: z.boolean(),
    creditBalanceSnapshot: creditBalanceSnapshotOutputSchema.nullish(),
    createdAt: z.date(),
    updatedAt: z.date(),
    // Agreement tracking
    signedClientAgreementId: z.string().uuid().nullish(),
    serviceTemplateName: z.string().nullish(),
    serviceTemplateType: z.string().nullish(),
    // Billing info from RecurringInvoice
    price: z.number().nullish(),
    currency: z.string().nullish(),
    // Refund status
    isRefunded: z.boolean().nullish(),
    // Invoice that created this service
    createdByInvoiceId: z.string().uuid(),
    // Staff user who referred the client for this service
    referredByStaffName: z.string().nullish(),
    // Scheduled cancellation (if any)
    scheduledCancellation: serviceCancellationSchema.nullish(),
    // Active suspension (if any) — endDate is null for indefinite suspensions
    activeSuspension: z.object({ endDate: z.string().nullish() }).nullish(),
    // Scheduled membership plan change (if any)
    scheduledPlanChange: z
      .object({
        servicePlanChangeId: z.uuid(),
        status: z.string(),
        effectiveDate: z.string(),
        toServiceTemplateId: z.uuid(),
        toServiceTemplateName: z.string().nullish(),
      })
      .nullish(),
    // Whether the service template allows plan changes
    changePlanEnabled: z.boolean().nullish(),
    // Cross-location waiting period (days from current cycle start) before this
    // service's credits may be consumed at a shared location. Null/0 = no gate.
    // Used by the client UI to show when a shared-in package becomes usable.
    minimumHomeUsageInDays: z.number().int().nullish(),
    // Date this service's credits become usable at a shared location for the current
    // cycle: COALESCE(current cycle's ServiceCreditAllocation.validFrom, startsAt) +
    // minimumHomeUsageInDays. Null when there is no waiting period. Computed from the
    // authoritative allocation table (not the UI snapshot) so it matches the booking gate.
    sharedHomeUsageUnlockDate: z.string().nullish(),
    // Most recent executed plan change (if any)
    lastExecutedPlanChange: z
      .object({
        servicePlanChangeId: z.uuid(),
        fromServiceTemplateName: z.string().nullish(),
        executedAt: z.date().nullish(),
        initiatedByStaffName: z.string().nullish(),
      })
      .nullish(),
    // Session types currently defined on the membership template (includes types not yet in any allocation)
    templateCreditSessionTypes: z.array(templateCreditSessionTypeSchema).nullish(),
    // Live cross-location sharing config, read from the current ServiceTemplate at resolve
    // time (not the denormalized Service.* copy, which goes stale on template edits / plan
    // changes). Used by the client UI to flag, on the home location's card, that this service
    // is shared OUT to other sites. `isAllSharedSites` = shared to every corporate site.
    isAllSharedSites: z.boolean().nullish(),
    sharedSiteIds: z.array(z.string().uuid()).nullish(),
  })
  .register(asObjectType, {
    name: 'ClientService',
    description: 'Client service with template info for UI display',
  })

export type ClientServicePublicSchema = z.infer<typeof clientServicePublicSchema>

export const ClientServicesSortBy = {
  ExpiresAt: 'expiresAt',
  StartsAt: 'startsAt',
  Name: 'name',
  CreatedAt: 'createdAt',
} as const

export type ClientServicesSortByValue =
  (typeof ClientServicesSortBy)[keyof typeof ClientServicesSortBy]

export const clientServicesSortByEnumSchema = z
  .enum(['expiresAt', 'startsAt', 'name', 'createdAt'])
  .register(asEnumType, { name: 'ClientServicesSortBy' })

export const SortOrder = {
  Asc: 'asc',
  Desc: 'desc',
} as const

export type SortOrderValue = (typeof SortOrder)[keyof typeof SortOrder]

export const sortOrderEnumSchema = z
  .enum(['asc', 'desc'])
  .register(asEnumType, { name: 'SortOrder' })

export const clientServicesPaginationArgsSchema = createPaginationWithCursorArgsSchema()
  .build()
  .extend({
    clientId: z.string().uuid(),
    siteId: z.string().uuid().optional(),
    search: z.string().optional(),
    status: z.array(serviceStatusEnumSchema).optional(),
    type: z.array(z.string()).optional(),
    siteSessionTypeId: z.string().uuid().optional(),
    sortBy: clientServicesSortByEnumSchema.optional(),
    sortOrder: sortOrderEnumSchema.optional(),
    sharedSiteIds: z.array(z.string().uuid()).optional(),
    serviceIds: z.array(z.string().uuid()).optional(),
  })
  .register(asObjectType, {
    name: 'ClientServicesPaginationArgs',
    description: 'Pagination arguments for client services query',
  })

export type ClientServicesPaginationArgsSchema = z.infer<typeof clientServicesPaginationArgsSchema>

export const clientServicesPaginationResultSchema = createPaginationWithCursorResultSchema(
  clientServicePublicSchema
).register(asObjectType, {
  name: 'ClientServicesPaginationResult',
  description: 'Paginated client services result',
})

export type ClientServicesPaginationResultSchema = z.infer<
  typeof clientServicesPaginationResultSchema
>

/**
 * Credit Usage History - For Visits History dialog
 */
export const creditUsageEntrySchema = z
  .object({
    ledgerId: z.string().uuid(),
    bookingId: z.string().uuid().nullish(),
    siteId: z.string().uuid(),
    siteSessionTypeId: z.string().uuid().nullish(),
    amount: z.number().int().nullish(),
    direction: serviceCreditLedgerDirectionEnumSchema,
    transactionType: serviceCreditLedgerTransactionTypeEnumSchema,
    notes: z.string().nullish(),
    createdAt: z.date(),
    bookingStatus: BookingStatusEnum.nullish(),
    sessionName: z
      .string()
      .nullish()
      .describe('Name of the session associated with this credit usage'),
    sessionDate: z.string().nullish().describe('Date of the session (YYYY-MM-DD)'),
    sessionStartTime: z.coerce.date().nullish().describe('Start time of the session'),
    isBuddyBooking: z
      .boolean()
      .nullish()
      .describe('True when the booking belongs to a client other than the credit owner'),
  })
  .register(asObjectType, {
    name: 'CreditUsageEntry',
    description: 'Credit usage entry for visits history',
  })

export type CreditUsageEntrySchema = z.infer<typeof creditUsageEntrySchema>

export const creditUsageHistoryResultSchema = createPaginationWithCursorResultSchema(
  creditUsageEntrySchema
).register(asObjectType, {
  name: 'CreditUsageHistoryResult',
  description: 'Paginated credit usage history result',
})

export type CreditUsageHistoryResultSchema = z.infer<typeof creditUsageHistoryResultSchema>

/**
 * Credit Adjustment Input
 */
export const adjustCreditsInputSchema = z
  .object({
    serviceId: z.string().uuid(),
    siteId: z.string().uuid(),
    amount: z.number().int().positive(),
    direction: serviceCreditLedgerDirectionEnumSchema,
    notes: z.string().optional(),
    reason: z.string().optional(),
    siteSessionTypeId: z
      .string()
      .uuid()
      .optional()
      .describe(
        'Optional session type ID to adjust credits for. If not provided, adjusts the first LIMITED allocation.'
      ),
    periodId: z
      .string()
      .optional()
      .describe(
        'Period ID for the credit adjustment. Can be a real UUID or "virtual-N" for future periods that need materialization.'
      ),
  })
  .register(asObjectType, {
    name: 'AdjustCreditsInput',
    description: 'Input for manual credit adjustment',
  })

export type AdjustCreditsInputSchema = z.infer<typeof adjustCreditsInputSchema>

/**
 * Credit Adjustment Note Entry
 * Represents a manual credit adjustment entry for display in notes history
 */
export const creditAdjustmentNoteSchema = z
  .object({
    note: z.string(),
    timestamp: z.string(), // ISO timestamp
    authorId: z.string().nullable(),
    amount: z.number(),
    direction: serviceCreditLedgerDirectionEnumSchema,
  })
  .register(asObjectType, {
    name: 'CreditAdjustmentNote',
    description: 'A credit adjustment note entry',
  })

export type CreditAdjustmentNoteSchema = z.infer<typeof creditAdjustmentNoteSchema>

/**
 * Credit Adjustment Notes Result
 * Paginated result for notes history in UpdateCreditsDialog
 */
export const creditAdjustmentNotesResultSchema = z
  .object({
    notes: z.array(creditAdjustmentNoteSchema),
    hasMore: z.boolean(),
    endCursor: z.string().nullable(),
  })
  .register(asObjectType, {
    name: 'CreditAdjustmentNotesResult',
    description: 'Paginated credit adjustment notes result',
  })

export type CreditAdjustmentNotesResultSchema = z.infer<typeof creditAdjustmentNotesResultSchema>

/**
 * Per-session-type credit breakdown within a single period.
 *
 * Returned by `serviceCreditSummaryPeriods` (MA-6 extension) so callers don't
 * need to fan out N queries (one per session type) and re-aggregate.
 *
 * Aggregation matches legacy `aggregateAllocsBySessionType` semantics: one row
 * per *session-type-name group*. A pooled allocation (`siteSessionTypeIds`
 * holding multiple IDs) emits ONE row whose `sessionTypeName` is the joined
 * names (e.g. "1 on 1 PT, Group Fitness") and whose `creditsAllocated` reflects
 * the pool's shared amount — NOT the pool amount × N members. This is the
 * mobile/embed contract.
 *
 * Shape notes:
 *   - `siteSessionTypeIds` is the canonical list of types this row covers.
 *     Empty array (`[]`) signals an unscoped/global allocation.
 *   - `sessionTypeName` is the human-readable joined string, or null for
 *     global allocations or when name lookups all failed.
 *   - UNLIMITED rows report `creditsRemaining = UNLIMITED_CREDITS_SENTINEL`
 *     (999999); the V1 legacy contract translates to its own sentinel at the
 *     response boundary.
 */
export const creditSummaryPeriodSessionTypeBreakdownSchema = z
  .object({
    siteSessionTypeIds: z.array(z.string()),
    sessionTypeName: z.string().nullable(),
    creditsAllocated: z.number().int(),
    creditsConsumed: z.number().int(),
    creditsRemaining: z.number().int(),
    isUnlimited: z.boolean(),
  })
  .register(asObjectType, {
    name: 'CreditSummaryPeriodSessionTypeBreakdown',
    description: 'Per-session-type credit allocation breakdown within a period',
  })

export type CreditSummaryPeriodSessionTypeBreakdownSchema = z.infer<
  typeof creditSummaryPeriodSessionTypeBreakdownSchema
>

/**
 * Credit Summary Period - Individual billing period with credit allocation data
 * Used by the Credits Summary dialog to display period-by-period credit breakdown
 *
 * MA-6: additive extension — `isActive` flags the period covering today (UTC),
 * `sessionTypeBreakdown[]` exposes per-session-type rollup so legacy consumers
 * stop aggregating themselves. Both fields are optional; existing callers
 * ignore them without code change.
 */
export const creditSummaryPeriodSchema = z
  .object({
    periodId: z.string(),
    periodNumber: z.number().int(),
    periodStartDate: z.string().nullable(),
    periodEndDate: z.string().nullable(),
    creditsAllocated: z.number().int(),
    creditsConsumed: z.number().int(),
    hasRefund: z.boolean(),
    isMaterialized: z.boolean(),
    isUnlimited: z.boolean(),
    isActivationPending: z.boolean().optional(),
    activationExpiryDescription: z.string().optional(),
    isActive: z.boolean().optional(),
    sessionTypeBreakdown: z.array(creditSummaryPeriodSessionTypeBreakdownSchema).optional(),
  })
  .register(asObjectType, {
    name: 'CreditSummaryPeriod',
    description: 'Individual billing period with credit allocation data for credits summary',
  })

export type CreditSummaryPeriodSchema = z.infer<typeof creditSummaryPeriodSchema>

/**
 * Credit Summary Result - All billing periods for a service
 * No pagination needed - period count is bounded by billing cycles (typically 12-60 max)
 */
export const creditSummaryResultSchema = z
  .object({
    periods: z.array(creditSummaryPeriodSchema),
    totalPeriods: z.number().int(),
  })
  .register(asObjectType, {
    name: 'CreditSummaryResult',
    description: 'All billing periods with credit data for a service',
  })

export type CreditSummaryResultSchema = z.infer<typeof creditSummaryResultSchema>

/**
 * MA-6: per-package booking-context shape returned by the extended
 * `creditsCoverSession` query. One entry per eligible service for the
 * requested booking date + session type.
 *
 * Mirrors the legacy V1 `CreditPackageInfo` shape (api server
 * credits-validation.service.ts) one-to-one so the legacy /verifycredits
 * handler becomes a thin field-rename mapper. UNLIMITED credits use the
 * Core sentinel `UNLIMITED_CREDITS_SENTINEL` (999999); the V1 mapper
 * translates to its own sentinel at the response boundary.
 *
 * Fields with no per-period concept (e.g. `packageStatus`) carry the
 * derived display string Core produces — legacy does NOT remap status text.
 */
export const clientBookingPackageCreditDetailSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  totalCreditsRemaining: z.number().int(),
})

export type ClientBookingPackageCreditDetailSchema = z.infer<
  typeof clientBookingPackageCreditDetailSchema
>

export const clientBookingPackageSuspensionPeriodSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
})

export type ClientBookingPackageSuspensionPeriodSchema = z.infer<
  typeof clientBookingPackageSuspensionPeriodSchema
>

export const clientBookingPackageSchema = z
  .object({
    packageSubscriptionId: z.string(),
    packageId: z.string(),
    packageName: z.string(),
    packageCategory: z.string().nullable(),
    packageStatus: z.string(),
    expiryDate: z.string().nullable(),
    canceledAt: z.string().nullable(),
    packageAgreementAccepted: z.boolean(),
    creditsRemaining: z.number().int(),
    creditDetails: z.array(clientBookingPackageCreditDetailSchema),
    suspensionPeriod: z.array(clientBookingPackageSuspensionPeriodSchema),
  })
  .register(asObjectType, {
    name: 'ClientBookingPackage',
    description:
      'Per-package booking-context data returned by creditsCoverSession for the requested booking date',
  })

export type ClientBookingPackageSchema = z.infer<typeof clientBookingPackageSchema>

/**
 * MA-6: extended `creditsCoverSession` return shape. Existing fields
 * (`hasCredits`, `creditsAvailable`) are unchanged; `packages` is added as a
 * non-optional array (empty when no eligible service exists) so legacy
 * /verifycredits can map without checking for undefined.
 */
export const creditsCoverSessionResultSchema = z
  .object({
    hasCredits: z.boolean(),
    creditsAvailable: z.number(),
    packages: z.array(clientBookingPackageSchema),
  })
  .register(asObjectType, {
    name: 'CreditsCoverSessionResult',
    description:
      'Booking-gate result for a client + session + booking date, with rich per-package detail',
  })

export type CreditsCoverSessionResultSchema = z.infer<typeof creditsCoverSessionResultSchema>

export const clientServiceStatusDataSchema = z.object({
  clientId: z.string().uuid(),
  status: serviceStatusEnumSchema,
  templateType: serviceTemplateTypeEnumSchema,
  isIntroOffer: z.boolean(),
})

export type ClientServiceStatusDataSchema = z.infer<typeof clientServiceStatusDataSchema>
